/**
 * The Technology ROI Calculator — Halil's fourth tool, ported 2026-08-29.
 *
 * Source: `roi-calculator.html`, sent on Teams that morning with two placements
 * asked for — under the DAIMI block on Strategy Consulting, and in the tools
 * hub. It arrived as a standalone page with its own palette, its own Segoe UI
 * stack and its own name/email gate; what is kept here is the part that is
 * actually his, which is the model and the copy. The chrome is the site's.
 *
 * WHAT CHANGED ON THE WAY IN, and why:
 *
 * - **The gate.** The source collected a name and an email inline and then
 *   `console.log`ged them next to a commented-out `fetch`. Nothing would have
 *   reached anybody. It now goes through the shared `LeadGate`, which is the
 *   only write path the other three tools use — so this one arrives in
 *   `website.tool_submissions`, opens a task for Fatih, and carries the same
 *   Turnstile, honeypot and consent as its siblings. That also adds a Company
 *   field, which the table requires and the source never asked for.
 *
 * - **The numbers are untouched.** `CFG`, the twenty industry rows, the region
 *   factors, the country list and the objective modifiers are Halil's, verbatim,
 *   including the calibration comment. This file is the place to argue with
 *   them; the component does no arithmetic of its own beyond what is here.
 *
 * - **The price stays visible.** `COST_EMERGING` / `COST_DEVELOPED` are
 *   Digitopia's own engagement prices, and the source comments them "never
 *   displayed". They are displayed, in effect: "Your Digitopia ROI" is
 *   `total ÷ price`, printed beside the total, so two numbers on the page give
 *   the price by division. Raised with Kagan before porting, 2026-08-29:
 *   *"parayı görmelerinde sıkıntı yok, örnek hesaplama"* — so it is deliberate,
 *   not an oversight, and this comment exists so nobody "fixes" it later.
 */

/* Calibrated against a real value case: $1B discrete manufacturer, $25M IT
   budget (45% Grow+Transform) → $45.5M tangible (≈4x G&T budget), $25.7M
   intangible (56% of tangible), $33.2M counterfactual risk (47% of
   tangible+intangible) = 4.18x complete value ratio on total IT investment. */
export const ROI_CFG = {
  /** Tangible value = Grow-and-Transform budget × multiple × objective modifier. */
  VALUE_MULTIPLE: 4.0,
  /** Of tangible. Spec range 50–80%; the case came out at 56%. */
  INTANGIBLE_RATIO: 0.56,
  /** Of tangible + intangible. Spec range 40–70%; the case came out at 47%. */
  OPPORTUNITY_RATIO: 0.47,
  /** Digitopia's engagement price. Shown by implication — see the header. */
  COST_EMERGING: 30000,
  COST_DEVELOPED: 45000,
  /** Cross-region median IT spend as a percentage of revenue. */
  REGION_MEDIAN: 3.2,
}

export interface Industry {
  name: string
  /** IT spend as a percentage of revenue. */
  it: number
  /** Run / Grow / Transform split of that budget, in percent. */
  run: number
  grow: number
  tr: number
  /** The one measure this industry argues about in its own board meetings. */
  kpi: string
  kdesc: string
}

export const ROI_INDUSTRIES: Record<string, Industry> = {
  banking: {
    name: 'Banking & Financial Services', it: 6.8, run: 68, grow: 21, tr: 11,
    kpi: 'Cost / Income (C/I) Ratio',
    kdesc: 'Digital leaders in banking run C/I ratios 5–10 points below peers. Even a 1-point improvement on your cost base typically dwarfs the entire transformation budget.',
  },
  software: {
    name: 'Software & Internet Services', it: 6.7, run: 70, grow: 20, tr: 10,
    kpi: 'Net Revenue Retention (NRR)',
    kdesc: 'Every point of NRR improvement compounds annually. Systematic transformation focuses product, data and GTM investments where retention is won.',
  },
  education: {
    name: 'Education', it: 5.8, run: 76, grow: 16, tr: 8,
    kpi: 'Enrollment & Student Retention',
    kdesc: 'Digital-mature institutions convert and retain more students at lower acquisition cost.',
  },
  media: {
    name: 'Media & Entertainment', it: 5.4, run: 67, grow: 24, tr: 10,
    kpi: 'ARPU & Audience Retention',
    kdesc: 'Data-driven content and personalization lift revenue per user and reduce churn.',
  },
  healthcare: {
    name: 'Healthcare Providers', it: 4.7, run: 76, grow: 16, tr: 8,
    kpi: 'Cost per Patient Episode',
    kdesc: 'Digitally mature providers treat more patients with the same capacity and reduce cost per episode while improving outcomes.',
  },
  telecom: {
    name: 'Telecommunications', it: 4.1, run: 65, grow: 23, tr: 12,
    kpi: 'ARPU & Churn Rate',
    kdesc: 'Transformation leaders monetize networks and data faster and lose fewer customers.',
  },
  profserv: {
    name: 'Professional Services', it: 4.1, run: 74, grow: 16, tr: 10,
    kpi: 'Utilization & Revenue per Professional',
    kdesc: 'AI-augmented delivery raises billable utilization and revenue per professional.',
  },
  utilities: {
    name: 'Utilities', it: 3.7, run: 70, grow: 23, tr: 7,
    kpi: 'Cost-to-Serve & Grid Reliability (SAIDI)',
    kdesc: 'Smart operations reduce cost-to-serve and outage minutes simultaneously.',
  },
  pharma: {
    name: 'Pharmaceuticals & Life Sciences', it: 3.6, run: 74, grow: 18, tr: 8,
    kpi: 'Time-to-Market / R&D Cycle Time',
    kdesc: 'Each month shaved off development timelines is worth multiples of the transformation budget in earlier revenue.',
  },
  insurance: {
    name: 'Insurance', it: 3.6, run: 69, grow: 21, tr: 10,
    kpi: 'Combined Ratio',
    kdesc: 'Data and AI maturity improves pricing, claims and fraud detection, and points off the combined ratio flow straight to profit.',
  },
  transport: {
    name: 'Transportation & Logistics', it: 3.3, run: 70, grow: 19, tr: 11,
    kpi: 'On-Time Performance & Cost per Shipment',
    kdesc: 'Visibility and optimization lift asset utilization and delivery reliability.',
  },
  consumer: {
    name: 'Consumer Products', it: 3.0, run: 72, grow: 19, tr: 8,
    kpi: 'Forecast Accuracy & OTIF',
    kdesc: 'Better demand sensing cuts stockouts and waste while protecting shelf availability.',
  },
  indelec: {
    name: 'Industrial Electronics & Electrical Equipment', it: 2.8, run: 76, grow: 15, tr: 9,
    kpi: 'OEE (Overall Equipment Effectiveness)',
    kdesc: 'Each OEE point recovered is capacity you already paid for. Digital leaders run 10+ points above laggards.',
  },
  manufacturing: {
    name: 'Industrial Manufacturing', it: 2.3, run: 71, grow: 20, tr: 9,
    kpi: 'OEE (Overall Equipment Effectiveness)',
    kdesc: 'Each OEE point recovered is capacity you already paid for. Digital leaders run 10+ points above laggards.',
  },
  chemicals: {
    name: 'Chemicals', it: 1.8, run: 73, grow: 19, tr: 8,
    kpi: 'Asset Utilization & Yield',
    kdesc: 'Process optimization and predictive quality convert directly into margin per ton.',
  },
  food: {
    name: 'Food & Beverage Processing', it: 1.6, run: 75, grow: 17, tr: 8,
    kpi: 'OEE & Waste Ratio',
    kdesc: 'Line efficiency and waste reduction are the fastest tangible wins in food processing.',
  },
  retail: {
    name: 'Retail & Wholesale', it: 1.6, run: 72, grow: 19, tr: 9,
    kpi: 'Sales per Square Meter',
    kdesc: 'Omnichannel maturity lifts sales density and inventory turns, the two levers that decide retail profitability.',
  },
  construction: {
    name: 'Construction & Materials', it: 1.4, run: 78, grow: 15, tr: 7,
    kpi: 'Project Margin & Rework Rate',
    kdesc: 'Digital project control reduces rework and claims, the silent killers of project margin.',
  },
  energy: {
    name: 'Energy', it: 1.2, run: 78, grow: 14, tr: 8,
    kpi: 'Production Uptime & Cost per Unit',
    kdesc: 'Predictive operations protect uptime, the single largest value lever in energy.',
  },
  other: {
    name: 'Other / Cross-Industry', it: 3.2, run: 73, grow: 18, tr: 9,
    kpi: 'EBITDA Margin',
    kdesc: 'Across industries, digitally mature companies grow faster and convert more revenue to profit than their peers.',
  },
}

/** IT spend as a percentage of revenue, by region — relative to REGION_MEDIAN. */
export const ROI_REGIONS: Record<string, number> = { na: 3.5, emea: 3.2, apac: 2.6, latam: 2.3 }

/**
 * Country → region and economy class. The economy class picks which engagement
 * price the "Digitopia ROI" is computed against; it is never asked for directly.
 */
export const ROI_COUNTRIES: [name: string, region: string, economy: 'developed' | 'emerging'][] = [
  ['Türkiye', 'emea', 'emerging'], ['United States', 'na', 'developed'],
  ['United Kingdom', 'emea', 'developed'], ['Germany', 'emea', 'developed'],
  ['France', 'emea', 'developed'], ['Netherlands', 'emea', 'developed'],
  ['Belgium', 'emea', 'developed'], ['Switzerland', 'emea', 'developed'],
  ['Austria', 'emea', 'developed'], ['Italy', 'emea', 'developed'],
  ['Spain', 'emea', 'developed'], ['Portugal', 'emea', 'developed'],
  ['Ireland', 'emea', 'developed'], ['Nordics (SE/NO/DK/FI)', 'emea', 'developed'],
  ['Poland', 'emea', 'emerging'], ['Czechia', 'emea', 'emerging'],
  ['Romania', 'emea', 'emerging'], ['Greece', 'emea', 'emerging'],
  ['United Arab Emirates', 'emea', 'developed'], ['Saudi Arabia', 'emea', 'emerging'],
  ['Qatar', 'emea', 'developed'], ['Kuwait', 'emea', 'emerging'],
  ['Egypt', 'emea', 'emerging'], ['South Africa', 'emea', 'emerging'],
  ['Nigeria', 'emea', 'emerging'], ['Kenya', 'emea', 'emerging'],
  ['Israel', 'emea', 'developed'], ['Canada', 'na', 'developed'],
  ['Mexico', 'latam', 'emerging'], ['Brazil', 'latam', 'emerging'],
  ['Argentina', 'latam', 'emerging'], ['Chile', 'latam', 'emerging'],
  ['Colombia', 'latam', 'emerging'], ['India', 'apac', 'emerging'],
  ['China', 'apac', 'emerging'], ['Japan', 'apac', 'developed'],
  ['South Korea', 'apac', 'developed'], ['Singapore', 'apac', 'developed'],
  ['Hong Kong', 'apac', 'developed'], ['Australia', 'apac', 'developed'],
  ['New Zealand', 'apac', 'developed'], ['Indonesia', 'apac', 'emerging'],
  ['Malaysia', 'apac', 'emerging'], ['Thailand', 'apac', 'emerging'],
  ['Vietnam', 'apac', 'emerging'], ['Philippines', 'apac', 'emerging'],
  ['Pakistan', 'apac', 'emerging'], ['Kazakhstan', 'emea', 'emerging'],
  ['Azerbaijan', 'emea', 'emerging'], ['Other (Developed Economy)', 'emea', 'developed'],
  ['Other (Emerging Economy)', 'emea', 'emerging'],
]

/** Business objective → benefit modifier and the narrative angle it argues. */
export const ROI_OBJECTIVES: Record<string, { mod: number; note: string }> = {
  Growth: { mod: 1.20, note: 'revenue expansion through digital channels, products and markets' },
  'Cost Saving': { mod: 1.00, note: 'efficiency and automation across operations' },
  'Operational Excellence': { mod: 1.10, note: 'process performance, quality and reliability' },
  'Customer Centricity': { mod: 1.15, note: 'experience, retention and lifetime value' },
  'Technology Transformation': { mod: 1.15, note: 'modern platforms, data and AI foundations' },
  'Cultural Transformation': { mod: 0.90, note: 'capabilities, ways of working and adoption' },
  Other: { mod: 1.00, note: 'your specific strategic agenda' },
}

/**
 * The words. Halil's, trimmed only where the site's own chrome already says it.
 *
 * The industry, country and objective names live here as well as in the data
 * above, and that is why: the tables carry the ARITHMETIC and are keyed by a
 * stable English key, while the dictionary carries what a reader sees. A locale
 * overrides `industries.banking.name` and the model is untouched. The English
 * maps are derived from the tables rather than retyped, so there is still one
 * place to change an English industry name.
 */
export const roiCalculator = {
  form: {
    revenue: 'Annual revenue (USD)',
    revenuePlaceholder: 'e.g. 250,000,000',
    country: 'Country',
    industry: 'Industry',
    objective: 'Business objective',
    objectiveHint: 'select all that apply',
    submit: 'Calculate ROI',
    privacy: 'Your inputs are not stored until you choose to receive your report.',
    needRevenue: 'Please enter your annual revenue in USD (at least $1M).',
    needObjective: 'Please choose at least one business objective.',
  },

  /** Industry names and their KPI copy, keyed by the table's key. */
  industries: Object.fromEntries(
    Object.entries(ROI_INDUSTRIES).map(([key, i]) => [
      key,
      { name: i.name, kpi: i.kpi, kdesc: i.kdesc },
    ]),
  ) as Record<string, { name: string; kpi: string; kdesc: string }>,

  /** Country names, keyed by the English name in `ROI_COUNTRIES`. */
  countries: Object.fromEntries(
    ROI_COUNTRIES.map(([name]) => [name, name]),
  ) as Record<string, string>,

  /** Objective chips and the narrative angle each one argues. */
  objectives: Object.fromEntries(
    Object.entries(ROI_OBJECTIVES).map(([key, o]) => [key, { label: key, note: o.note }]),
  ) as Record<string, { label: string; note: string }>,

  teaser: {
    eyebrow: 'YOUR VALUE POTENTIAL',
    heading: 'Your technology investment can return multiples: here is the tangible part',
    /* The sentences the component used to hold as template literals. `{name}`
       placeholders, filled by `fill()` in RoiCalculator.tsx — a locale can put
       them in whatever order its grammar wants. */
    basis:
      'Basis: {industry} companies typically spend about {pct}% of revenue on technology '
      + '(adjusted for {country}), which is an estimated {budget} a year for you. The budget '
      + 'is the input; the value it returns is what matters.',
    gtLabel: 'Value-creating tech budget',
    gtNote:
      'The Grow & Transform share of your tech budget. The remaining {run}% runs the '
      + 'business: necessary, but it creates no new value.',
    tangibleLabel: 'Tangible value potential / year',
    tangibleNote:
      'Measurable, directly attributable returns: operational efficiency, margin expansion, new digital revenue.',
    ratioLabel: 'Complete value ratio',
    ratioNote:
      'Every $1 of technology spend can return this much in complete value, counting tangible, intangible and avoided risk. Unlock the full breakdown below.',
    run: 'Run',
    grow: 'Grow',
    transform: 'Transform',
    unlock: 'See my full results',
  },

  report: {
    eyebrow: 'YOUR FULL RESULTS',
    heading: 'Your annual technology value potential',
    assumptions:
      'Assumptions: {multiple}x tangible return on your value-creating tech budget of '
      + '{budget}, adjusted for your objectives ({objectives}). Calibrated with industry '
      + 'benchmarks and real value cases.',
    tangible: 'Tangible benefits',
    tangibleBody:
      'We estimate {tangible} a year from focusing your Grow & Transform budget on '
      + '{objectives}, measured through {kpi}.',
    /** Joins the two objective notes in `tangibleBody`. Turkish wants "ve". */
    and: 'and',
    intangible: 'Intangible benefits',
    opportunity: 'Opportunity cost avoided',
    total: 'Total annual benefits',
    digiRoiLabel: 'Your Digitopia ROI',
    digiRoiNote:
      'Total annual value against your investment in Digitopia, less than {share}% of '
      + 'your technology budget.',
    ratioLabel: 'Complete value ratio of your tech investment',
    ratioNote:
      'Total annual value (tangible, intangible and avoided risk) for every dollar of your technology budget, when transformation is managed systematically instead of ad hoc.',
    intangibleBody: [
      '**Informed decisions.** What does one wrong or ill-informed strategic decision cost? Better data quality and decision intelligence prevent multi-million-dollar missteps.',
      '**Executive consensus.** Misaligned leadership teams burn budget on competing priorities. Alignment converts the same spend into compounding results.',
      '**Accelerated execution.** Every quarter a major investment slips, its return slips with it. Organizational agility pulls value forward.',
    ],
    intangibleAlso:
      'Also counted: employee experience, knowledge capital, brand and trust, ecosystem effects.',
    opportunityBody:
      'Waiting is also a decision, and it carries an expected cost: data debt, talent exodus, competitive displacement, regulatory exposure, supply-chain fragility and innovation stagnation.',
    opportunityEstimate:
      'For your organization we estimate the expected annual cost of not transforming at '
      + '{amount}, often more than the technology budget itself. Not investing is the '
      + 'most expensive option.',
    restart: 'Start again',
    ctaHeading: 'Numbers are a starting point. Your roadmap is the destination.',
    ctaBody:
      'In a 30-minute conversation we will walk through your results, benchmark you against 1,575 companies in our global dataset, and show what a DAIMI maturity measurement would reveal about your organization.',
    ctaLabel: 'Book a 30-minute conversation',
    disclaimer:
      'Estimates are calibrated with industry benchmarks and real value cases. Directional guidance, not a formal business case.',
  },
}
